import { Button, Input, message, Modal } from "antd";
import React, { CSSProperties, useEffect, useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import {
  useCreateFarmingStage,
  useGetFarmingStageById,
  useUpdateFarmingStage,
} from "../../api/farming_stage";
import {
  FarmingStageFormValues,
  farmingStageSchema,
} from "../../schemas/formSchema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

type FarmingStageModalProps = {
  open: boolean;
  selectedId?: number;
  selectedFarmingStage?: any;
  hideModal: () => void;
};

interface ITag {
  key: string;
  value: string;
}

const wrapper: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  marginTop: 25,
};
const label: CSSProperties = {
  fontSize: 15,
  color: "#C2CDD0",
};

const FarmingStageModal: React.FC<FarmingStageModalProps> = ({
  open,
  selectedId,
  hideModal,
}) => {
  const { TextArea } = Input;

  const [tags, setTags] = useState<ITag[]>([]);

  const [tagKey, setTagKey] = useState<string>();
  const [tagValue, setTagValue] = useState<string>();

  const { mutateAsync: createCenter } = useCreateFarmingStage();
  const { mutateAsync: updateStage } = useUpdateFarmingStage();
  const { data: selectedStage } = useGetFarmingStageById(selectedId);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FarmingStageFormValues>({
    resolver: zodResolver(farmingStageSchema),
    defaultValues: {
      name: "",
      description: "",
      tagKey: "",
    },
  });

  useEffect(() => {
    if (
      selectedStage &&
      selectedStage.data &&
      selectedStage.data.data.length > 0
    ) {
      const farmingStage = selectedStage.data.data[0];

      reset({
        name: farmingStage.stageName || "",
        description: farmingStage.description || "",
      });

      if (farmingStage.detail) {
        try {
          const val = JSON.parse(selectedStage.data.data[0].detail || "[]");
          setTags(val);
        } catch (err) {
          console.error("Error parsing detail JSON:", err);
          setTags([]);
        }
      } else {
        setTags([]);
      }
    } else {
      reset({
        name: "",
        description: "",
      });
      setTags([]);
    }
  }, [selectedStage, reset, open]);

  const handleInsertTag = () => {
    if (tagKey && tagValue) {
      const _tag = tags.find((tag) => tag.key === tagKey);

      if (!_tag) {
        setTags((prev) => [...prev, { key: tagKey, value: tagValue }]);
        setTagKey(undefined);
        setTagValue(undefined);
      }
    }
  };

  const handleRemoveTag = (key: string) => {
    if (key) {
      const _tags = tags.filter((tag) => tag.key !== key);
      setTags(_tags);
    }
  };

  const onSubmit = async (data: FarmingStageFormValues) => {
    if (!data) return;

    if (selectedId === -1) {
      try {
        const farmingData = {
          stageName: data.name,
          description: data.description,
          detail: JSON.stringify(tags),
        };

        const result = await createCenter(farmingData as any);

        if (result && result.data && result.data.data && result.data.data.id) {
          message.success("Farming stage updated successfully");
          reset();
        }
      } catch (err: any) {
        message.error("Operation failed");
      }
    } else {
      try {
        if (selectedId && selectedId > 0) {
          const result = await updateStage({
            id: selectedId,
            stage: {
              stageName: data.name,
              description: data.description,
              detail: JSON.stringify(tags),
            },
          });

          if (
            result &&
            result.data &&
            result.data.data &&
            result.data.data.id
          ) {
            message.success("Farming stage updated successfully");
            reset();
          }
        }
      } catch (err: any) {
        message.error("Operation failed");
      }

      hideModal();
    }
  };

  return (
    <Modal
      width={800}
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 27.43,
            color: "black",
          }}
        >
          {selectedId !== -1 ? "Edit Farming-Stage" : "Create Farming-Stage"}
        </div>
      }
      open={open}
      centered
      onOk={handleSubmit(onSubmit as any)}
      onCancel={hideModal}
      closable
    >
      <div className="flex-col md:flex justify-between gap-4">
        <div
          className="md:w-[50%]"
          style={{
            fontFamily: "Poppins",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input {...field} placeholder="Insert farming-stage name" />
            )}
          />

          {errors.name && (
            <div className="text-red-400">{errors.name.message}</div>
          )}

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea rows={4} placeholder="Description" {...field} />
            )}
          />
        </div>
        <div
          className="md:px-[50px] items-center"
          style={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid #E6EEF6",
            borderRadius: 4,
            gap: 12,
            paddingTop: 0,
            paddingBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginTop: -15,
            }}
          >
            {tags &&
              tags.map((tag: ITag) => (
                <div
                  key={tag.key}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #DCE2E4",
                    paddingTop: 20,
                    paddingBottom: 5,
                  }}
                >
                  <div className="text-black">{tag.key}</div>
                  <div className="text-black">
                    {tag.value}{" "}
                    <DeleteOutlined
                      style={{ color: "red" }}
                      onClick={() => handleRemoveTag(tag.key)}
                    />
                  </div>
                </div>
              ))}
          </div>
          <div
            className="flex justify-between md:flex-row"
            style={{
              gap: 12,
              width: "100%",
            }}
          >
            <div className="md:w-[50%]" style={{ ...wrapper, marginTop: 15 }}>
              <div style={label}>Key</div>
              <Input
                value={tagKey}
                onChange={(e) => setTagKey(e.target.value)}
                placeholder="Insert key"
              />
            </div>

            <div style={{ ...wrapper, width: "50%", marginTop: 15 }}>
              <div style={label}>Value</div>
              <Input
                value={tagValue}
                onChange={(e) => setTagValue(e.target.value)}
                placeholder="Insert value"
              />
            </div>
          </div>
          <Button style={{ width: 150 }} onClick={handleInsertTag}>
            Add new tag
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FarmingStageModal;
